import { useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useActiveFleet } from '@/data/fleets';
import {
  useCreateCreditNote,
  useDeleteDraft,
  useInvoice,
  useIssueCreditNote,
  useIssueInvoice,
  useRecordPayment,
  useRemovePayment,
  useVoidInvoice,
} from '@/data/invoices';
import { Spinner } from '@/components/ui/spinner';
import { EmptyState } from '@/components/empty-state';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog } from '@/components/ui/dialog';
import { Field, Input, Textarea } from '@/components/ui/input';
import { formatDate, formatMoney } from '@/lib/format';
import type { Invoice, InvoiceDisplayStatus, VatBand } from '@breaker/shared-types';
import type { StatusTone } from '@/lib/status';

const STATUS_TONE: Record<InvoiceDisplayStatus, StatusTone> = {
  draft: 'neutral',
  sent: 'info',
  part_paid: 'active',
  paid: 'success',
  overdue: 'danger',
  credited: 'neutral',
  void: 'neutral',
};

const STATUS_LABEL: Record<InvoiceDisplayStatus, string> = {
  draft: 'Draft',
  sent: 'Sent',
  part_paid: 'Part paid',
  paid: 'Paid',
  overdue: 'Overdue',
  credited: 'Credited',
  void: 'Void',
};

const VAT_LABEL: Record<string, string> = {
  standard: 'VAT',
  reduced: 'VAT (reduced)',
  zero: 'Zero-rated',
  exempt: 'Exempt',
};

/**
 * The totals block groups by VAT treatment, matching the PDF. A single header
 * rate misstates any mixed-rate invoice, and zero-rated and exempt are both 0%
 * but must not be added together.
 */
function vatBands(invoice: Invoice): VatBand[] {
  const bands = new Map<string, VatBand>();
  for (const line of invoice.lines ?? []) {
    const key = `${line.vatCode}:${line.vatRate}`;
    const found = bands.get(key);
    if (found) {
      found.netPence += line.netPence;
      found.vatPence += line.vatPence;
    } else {
      bands.set(key, {
        vatCode: line.vatCode,
        vatRate: line.vatRate,
        netPence: line.netPence,
        vatPence: line.vatPence,
      });
    }
  }
  return [...bands.values()].sort((a, b) => Number(b.vatRate) - Number(a.vatRate));
}

export function InvoiceDetailPage() {
  const { fleet } = useActiveFleet();
  const fleetId = fleet?.id ?? null;
  const { invoiceId } = useParams({ from: '/authed/invoices/$invoiceId' });
  const navigate = useNavigate();
  const query = useInvoice(fleetId, invoiceId);

  const issue = useIssueInvoice(fleetId);
  const voidInvoice = useVoidInvoice(fleetId);
  const deleteDraft = useDeleteDraft(fleetId);
  const [paying, setPaying] = useState(false);
  const [crediting, setCrediting] = useState(false);

  if (query.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size="lg" />
      </div>
    );
  }
  if (query.isError || !query.data) {
    return (
      <div className="px-8 py-10">
        <EmptyState
          title="Could not load invoice"
          description={(query.error as Error)?.message ?? 'Not found.'}
        />
      </div>
    );
  }

  const { invoice, payments, creditNotes } = query.data;
  const status = invoice.settlement?.displayStatus ?? 'draft';
  const isDraft = invoice.status === 'draft';
  const supplier = invoice.supplierSnapshot;
  const customer = invoice.customerSnapshot;

  // Any of these can be refused by a database rule; the message that comes
  // back is written for the user, so it is shown verbatim.
  const actionError =
    (issue.error as Error | null) ??
    (voidInvoice.error as Error | null) ??
    (deleteDraft.error as Error | null);

  // Some refusals carry a route to the fix. An incomplete billing block is
  // the one that matters: the guard fires at the moment someone is trying to
  // bill, and telling them "billing details are missing" without saying which
  // or where is the worst version of a guard. The server sends the customer
  // id and the exact field list; this turns them into a link and a list.
  const fixContext = (() => {
    const problem = (actionError as { detail?: { code?: string; errors?: unknown } } | null)
      ?.detail;
    if (problem?.code !== 'customer_billing_incomplete') return null;
    const e = problem.errors as
      | { customerId?: string; customerName?: string; missingFields?: string[] }
      | undefined;
    return e?.customerId ? e : null;
  })();

  const FIELD_LABEL: Record<string, string> = {
    billingAddressLine1: 'Billing address',
    billingPostcode: 'Postcode',
    billingEmail: 'Billing email',
    vatNumber: 'VAT number',
  };

  return (
    <div className="px-8 py-10 max-w-[900px]">
      <Link to="/invoices" className="text-sm text-breaker-mute hover:text-breaker-ink">
        ← Invoices
      </Link>

      <header className="mt-3 mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight text-breaker-ink">
              {invoice.numberText ?? 'Draft invoice'}
            </h1>
            <Badge tone={STATUS_TONE[status]}>{STATUS_LABEL[status]}</Badge>
          </div>
          <p className="text-sm text-breaker-mute mt-1">
            {customer?.name ?? '—'}
            {invoice.issuedAt ? ` · issued ${formatDate(invoice.issuedAt)}` : ''}
            {invoice.dueAt ? ` · due ${formatDate(invoice.dueAt)}` : ''}
          </p>
        </div>
        <div className="flex gap-2">
          {isDraft ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={async () => {
                  await deleteDraft.mutateAsync(invoice.id);
                  navigate({ to: '/invoices' });
                }}
              >
                Delete draft
              </Button>
              <Button
                size="sm"
                onClick={() => issue.mutate({ invoiceId: invoice.id })}
                disabled={issue.isPending}
              >
                {issue.isPending ? 'Issuing…' : 'Issue invoice'}
              </Button>
            </>
          ) : invoice.status !== 'void' ? (
            <>
              <Button variant="ghost" size="sm" onClick={() => setCrediting(true)}>
                Credit note
              </Button>
              <Button size="sm" onClick={() => setPaying(true)}>
                Record payment
              </Button>
            </>
          ) : null}
        </div>
      </header>

      {actionError ? (
        <div className="mb-5 rounded border border-breaker-red bg-breaker-red/10 px-4 py-3 text-sm text-breaker-red">
          <p>{actionError.message}</p>
          {fixContext ? (
            <>
              <ul className="mt-2 list-disc list-inside">
                {(fixContext.missingFields ?? []).map((f) => (
                  <li key={f}>{FIELD_LABEL[f] ?? f}</li>
                ))}
              </ul>
              <Link
                to="/customers/$customerId"
                params={{ customerId: fixContext.customerId! }}
                className="mt-2 inline-block font-medium underline"
              >
                Add these to {fixContext.customerName ?? 'the customer'} →
              </Link>
            </>
          ) : null}
        </div>
      ) : null}

      {isDraft ? (
        <div className="mb-5 rounded border border-breaker-line-2 bg-breaker-ground-2 px-4 py-3 text-sm text-breaker-ink-2">
          This is a draft. It has no number yet, and nothing has been sent. Once
          issued it cannot be edited — corrections are made with a credit note.
        </div>
      ) : null}

      {/* ---- Parties, as frozen on the document -------------------------- */}
      <div className="surface rounded-lg p-5 mb-5 grid grid-cols-2 gap-6 text-sm">
        <div>
          <h2 className="text-2xs uppercase tracking-wider text-breaker-mute mb-2">From</h2>
          <p className="font-medium text-breaker-ink">
            {supplier?.tradingName ?? supplier?.legalName ?? fleet?.legalName ?? '—'}
          </p>
          <div className="text-breaker-ink-2 mt-1 space-y-0.5">
            {supplier?.addressLine1 ? <p>{supplier.addressLine1}</p> : null}
            {supplier?.city || supplier?.postcode ? (
              <p>{[supplier?.city, supplier?.postcode].filter(Boolean).join(' ')}</p>
            ) : null}
            {supplier?.vatRegistered && supplier.vatNumber ? (
              <p>VAT {supplier.vatNumber}</p>
            ) : null}
          </div>
        </div>
        <div>
          <h2 className="text-2xs uppercase tracking-wider text-breaker-mute mb-2">
            Billed to
          </h2>
          <p className="font-medium text-breaker-ink">{customer?.name ?? '—'}</p>
          <div className="text-breaker-ink-2 mt-1 space-y-0.5">
            {customer?.addressLine1 ? <p>{customer.addressLine1}</p> : null}
            {customer?.city || customer?.postcode ? (
              <p>{[customer?.city, customer?.postcode].filter(Boolean).join(' ')}</p>
            ) : null}
            {customer?.vatNumber ? <p>VAT {customer.vatNumber}</p> : null}
          </div>
        </div>
      </div>

      {/* ---- Lines ------------------------------------------------------- */}
      <div className="surface rounded-lg overflow-hidden mb-5">
        <table className="w-full text-sm">
          <thead className="text-2xs uppercase tracking-wider text-breaker-mute border-b border-breaker-line-2">
            <tr>
              <th className="text-left font-medium px-4 py-2.5">Load</th>
              <th className="text-left font-medium px-4 py-2.5">Description</th>
              <th className="text-right font-medium px-4 py-2.5">VAT</th>
              <th className="text-right font-medium px-4 py-2.5">Net</th>
            </tr>
          </thead>
          <tbody>
            {(invoice.lines ?? []).map((line) => (
              <tr key={line.id} className="border-b border-breaker-line-2 last:border-0">
                <td className="px-4 py-3 text-breaker-ink-2">{line.loadReference ?? '—'}</td>
                <td className="px-4 py-3 text-breaker-ink">
                  {line.description}
                  {line.quantity !== 1 ? (
                    <span className="text-breaker-mute">
                      {' '}
                      · {line.quantity} × {formatMoney(line.unitPricePence)}
                    </span>
                  ) : null}
                </td>
                <td className="px-4 py-3 text-right text-breaker-ink-2">
                  {line.vatCode === 'zero' || line.vatCode === 'exempt'
                    ? VAT_LABEL[line.vatCode]
                    : `${Math.round(Number(line.vatRate) * 100)}%`}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {formatMoney(line.netPence)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t border-breaker-line-2">
            <tr>
              <td colSpan={3} className="px-4 py-2 text-right text-breaker-mute">
                Net
              </td>
              <td className="px-4 py-2 text-right tabular-nums">
                {formatMoney(invoice.netPence)}
              </td>
            </tr>
            {supplier?.vatRegistered !== false
              ? vatBands(invoice).map((band) => (
                  <tr key={`${band.vatCode}:${band.vatRate}`}>
                    <td colSpan={3} className="px-4 py-2 text-right text-breaker-mute">
                      {band.vatCode === 'zero' || band.vatCode === 'exempt'
                        ? `${VAT_LABEL[band.vatCode]} (${formatMoney(band.netPence)})`
                        : `${VAT_LABEL[band.vatCode]} at ${Math.round(Number(band.vatRate) * 100)}%`}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {formatMoney(band.vatPence)}
                    </td>
                  </tr>
                ))
              : null}
            <tr className="font-semibold text-breaker-ink">
              <td colSpan={3} className="px-4 py-2.5 text-right">
                Total
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums">
                {formatMoney(invoice.grossPence)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* ---- Settlement --------------------------------------------------- */}
      {!isDraft ? (
        <div className="surface rounded-lg p-5 mb-5">
          <h2 className="text-sm font-semibold text-breaker-ink mb-3">Settlement</h2>
          <dl className="grid grid-cols-3 gap-4 text-sm mb-4">
            <div>
              <dt className="text-breaker-mute text-2xs uppercase tracking-wider">Paid</dt>
              <dd className="tabular-nums mt-1">
                {formatMoney(invoice.settlement?.paidPence ?? 0)}
              </dd>
            </div>
            <div>
              <dt className="text-breaker-mute text-2xs uppercase tracking-wider">
                Credited
              </dt>
              <dd className="tabular-nums mt-1">
                {formatMoney(invoice.settlement?.creditedPence ?? 0)}
              </dd>
            </div>
            <div>
              <dt className="text-breaker-mute text-2xs uppercase tracking-wider">
                Outstanding
              </dt>
              <dd className="tabular-nums mt-1 font-medium">
                {formatMoney(invoice.settlement?.outstandingPence ?? 0)}
              </dd>
            </div>
          </dl>

          {payments.length > 0 ? (
            <PaymentsTable
              fleetId={fleetId}
              invoiceId={invoice.id}
              payments={payments}
            />
          ) : (
            <p className="text-sm text-breaker-mute">No payments recorded yet.</p>
          )}

          {creditNotes.length > 0 ? (
            <CreditNotesList fleetId={fleetId} notes={creditNotes} />
          ) : null}
        </div>
      ) : null}

      {paying ? (
        <RecordPaymentDialog
          fleetId={fleetId}
          invoiceId={invoice.id}
          outstandingPence={invoice.settlement?.outstandingPence ?? invoice.grossPence}
          onClose={() => setPaying(false)}
        />
      ) : null}
      {crediting ? (
        <CreditNoteDialog
          fleetId={fleetId}
          invoiceId={invoice.id}
          onClose={() => setCrediting(false)}
        />
      ) : null}
    </div>
  );
}

function PaymentsTable({
  fleetId,
  invoiceId,
  payments,
}: {
  fleetId: string | null;
  invoiceId: string;
  payments: Array<{
    id: string;
    receivedAt: string;
    amountPence: number;
    method: string;
    reference: string | null;
  }>;
}) {
  const remove = useRemovePayment(fleetId);
  return (
    <table className="w-full text-sm">
      <tbody>
        {payments.map((p) => (
          <tr key={p.id} className="border-b border-breaker-line-2 last:border-0">
            <td className="py-2 text-breaker-ink-2">{formatDate(p.receivedAt)}</td>
            <td className="py-2 text-breaker-ink-2">{p.method.replace(/_/g, ' ')}</td>
            <td className="py-2 text-breaker-ink-2">{p.reference ?? ''}</td>
            <td className="py-2 text-right tabular-nums">{formatMoney(p.amountPence)}</td>
            <td className="py-2 text-right">
              <button
                type="button"
                className="text-2xs uppercase tracking-wider text-breaker-mute hover:text-breaker-red"
                onClick={() => remove.mutate({ invoiceId, paymentId: p.id })}
              >
                Remove
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CreditNotesList({
  fleetId,
  notes,
}: {
  fleetId: string | null;
  notes: Array<{ id: string; numberText: string | null; status: string; grossPence: number; reason: string | null }>;
}) {
  const issue = useIssueCreditNote(fleetId);
  return (
    <div className="mt-4 pt-4 border-t border-breaker-line-2">
      <h3 className="text-2xs uppercase tracking-wider text-breaker-mute mb-2">
        Credit notes
      </h3>
      <ul className="space-y-2">
        {notes.map((n) => (
          <li key={n.id} className="flex items-center justify-between text-sm">
            <span className="text-breaker-ink">
              {n.numberText ?? 'Draft credit note'}
              {n.reason ? <span className="text-breaker-mute"> · {n.reason}</span> : null}
            </span>
            <span className="flex items-center gap-3">
              <span className="tabular-nums">{formatMoney(n.grossPence)}</span>
              {n.status === 'draft' ? (
                <Button size="sm" variant="ghost" onClick={() => issue.mutate(n.id)}>
                  Issue
                </Button>
              ) : (
                <Badge tone="neutral">Issued</Badge>
              )}
            </span>
          </li>
        ))}
      </ul>
      {issue.isError ? (
        <p className="text-sm text-breaker-red mt-2">{(issue.error as Error).message}</p>
      ) : null}
    </div>
  );
}

function RecordPaymentDialog({
  fleetId,
  invoiceId,
  outstandingPence,
  onClose,
}: {
  fleetId: string | null;
  invoiceId: string;
  outstandingPence: number;
  onClose: () => void;
}) {
  const record = useRecordPayment(fleetId);
  // Pre-filled with what is actually outstanding: the overwhelmingly common
  // case is settling in full, and typing a figure invites a typo the server
  // will only reject.
  const [amount, setAmount] = useState((outstandingPence / 100).toFixed(2));
  const [receivedAt, setReceivedAt] = useState(new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState('bank_transfer');
  const [reference, setReference] = useState('');

  async function submit() {
    await record.mutateAsync({
      invoiceId,
      receivedAt: new Date(receivedAt).toISOString(),
      // Pounds → pence at the boundary, so nothing downstream sees a float.
      amountPence: Math.round(Number(amount) * 100),
      method: method as never,
      reference: reference || null,
    });
    onClose();
  }

  return (
    <Dialog open onClose={onClose} title="Record a payment">
      <div className="space-y-4">
        <p className="text-sm text-breaker-mute">
          Recording what has arrived. Breaker does not collect the money.
        </p>
        <Field label="Amount (£)" htmlFor="payAmount">
          <Input
            id="payAmount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputMode="decimal"
          />
        </Field>
        <Field label="Received" htmlFor="payDate">
          <Input
            id="payDate"
            type="date"
            value={receivedAt}
            onChange={(e) => setReceivedAt(e.target.value)}
          />
        </Field>
        <Field label="Method" htmlFor="payMethod">
          <select
            id="payMethod"
            className="w-full rounded border border-breaker-line-2 bg-white px-3 py-2 text-sm"
            value={method}
            onChange={(e) => setMethod(e.target.value)}
          >
            <option value="bank_transfer">Bank transfer</option>
            <option value="card">Card</option>
            <option value="cash">Cash</option>
            <option value="cheque">Cheque</option>
            <option value="direct_debit">Direct debit</option>
            <option value="other">Other</option>
          </select>
        </Field>
        <Field label="Reference (optional)" htmlFor="payRef">
          <Input
            id="payRef"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
          />
        </Field>
        {record.isError ? (
          <p className="text-sm text-breaker-red">{(record.error as Error).message}</p>
        ) : null}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" onClick={submit} disabled={record.isPending}>
            {record.isPending ? 'Saving…' : 'Record payment'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function CreditNoteDialog({
  fleetId,
  invoiceId,
  onClose,
}: {
  fleetId: string | null;
  invoiceId: string;
  onClose: () => void;
}) {
  const create = useCreateCreditNote(fleetId);
  const [reason, setReason] = useState('');

  async function submit() {
    await create.mutateAsync({ invoiceId, reason: reason || null });
    onClose();
  }

  return (
    <Dialog open onClose={onClose} title="Raise a credit note">
      <div className="space-y-4">
        <p className="text-sm text-breaker-mute">
          An issued invoice cannot be edited, so corrections are made by
          crediting it. This drafts a credit note for the full amount — issue it
          when you are happy with it.
        </p>
        <Field label="Reason" htmlFor="creditReason">
          <Textarea
            id="creditReason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Overcharged, job cancelled, billed in error…"
            rows={3}
          />
        </Field>
        {create.isError ? (
          <p className="text-sm text-breaker-red">{(create.error as Error).message}</p>
        ) : null}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" onClick={submit} disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Draft credit note'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
